import { useCallback, useMemo } from 'react';

/**
 * useSprintDetailRenderHandlers
 *
 * Builds the render-time inline callbacks that would otherwise bloat
 * SprintDetail's JSX: gantt add-task/decision routing, deliverable
 * CRUD + submission, attachment count sync, custom-fields updates, and
 * the delete-modal confirm bridge.
 *
 * Pure orchestration — all business logic still lives in `actions`.
 */
export default function useSprintDetailRenderHandlers({
  modals,
  actions,
  tasks,
  state,
  setLocalSprint,
  handleConfirmDeleteTaskWithNested,
}) {
  const handleDeleteModalConfirm = useCallback(() => {
    const { taskId, deleteOptions } = modals.handleDeleteModalConfirm();
    if (taskId) handleConfirmDeleteTaskWithNested(taskId, deleteOptions || undefined);
  }, [modals, handleConfirmDeleteTaskWithNested]);

  // ─── Gantt add-task / add-decision routing by group/color ───
  const makeGanttAdd = useCallback((type) => (groupKey, colorBy) => {
    const options = {};
    if (colorBy === 'status' && groupKey) {
      return actions.handleAddTask(groupKey, null, type, options);
    } else if (colorBy === 'priority' && groupKey) {
      options.priority = groupKey;
      return actions.handleAddTask(null, null, type, options);
    } else if (colorBy === 'tag' && groupKey && groupKey !== '__empty__') {
      options.tags = [groupKey];
      return actions.handleAddTask(null, null, type, options);
    }
    return actions.handleAddTask(null, null, type, options);
  }, [actions]);

  const onGanttAddTask = useMemo(() => makeGanttAdd('task'), [makeGanttAdd]);
  const onGanttAddDecision = useMemo(() => makeGanttAdd('decision'), [makeGanttAdd]);
  const onGanttAddWait = useMemo(() => makeGanttAdd('wait'), [makeGanttAdd]);

  // ─── Deliverables CRUD ───
  const getDeliverables = useCallback((taskId) => {
    const task = tasks.find(t => t.id === taskId);
    return task?.deliverables || [];
  }, [tasks]);

  const onAddDeliverable = useCallback((taskId, data) => {
    const newDeliverable = {
      id: `del-${Date.now()}`,
      name: data.name,
      fileType: data.fileType,
      description: data.description,
      link: '',
    };
    actions.handleDeliverablesChange(taskId, [...getDeliverables(taskId), newDeliverable]);
  }, [actions, getDeliverables]);

  const onRemoveDeliverable = useCallback((taskId, deliverableId) => {
    const updated = getDeliverables(taskId).filter(d => d.id !== deliverableId);
    actions.handleDeliverablesChange(taskId, updated);
  }, [actions, getDeliverables]);

  const onUpdateDeliverable = useCallback((taskId, deliverableId, data) => {
    const updated = getDeliverables(taskId).map(d =>
      d.id === deliverableId ? { ...d, ...data } : d
    );
    actions.handleDeliverablesChange(taskId, updated);
  }, [actions, getDeliverables]);

  const onClearDeliverable = useCallback((taskId, deliverableId) => {
    const updated = getDeliverables(taskId).map(d =>
      d.id === deliverableId ? {
        ...d,
        completed: false,
        link: '',
        attachmentId: undefined,
        submittedFilename: undefined,
      } : d
    );
    actions.handleDeliverablesChange(taskId, updated);
  }, [actions, getDeliverables]);

  // ─── Attachments count sync ───
  const onAttachmentsChange = useCallback((newCount) => {
    if (state.attachmentsModalTaskId && typeof newCount === 'number') {
      actions.handleAttachmentCountChange(state.attachmentsModalTaskId, newCount);
    }
  }, [actions, state]);

  // ─── Custom fields ───
  const onCustomFieldsChange = useCallback((newCustomFieldsOrUpdater) => {
    setLocalSprint(prev => {
      if (!prev) return prev;
      const newCustomFields = typeof newCustomFieldsOrUpdater === 'function'
        ? newCustomFieldsOrUpdater(prev.customFields || [])
        : newCustomFieldsOrUpdater;
      return { ...prev, customFields: newCustomFields };
    });
  }, [setLocalSprint]);

  // ─── Deliverable submission modal ───
  const openSubmitModal = useCallback((deliverable) => {
    const task = tasks.find(t => t.id === state.detailSidebarTaskId);
    state.setDeliverableSubmitModal({
      isOpen: true,
      taskId: state.detailSidebarTaskId,
      taskName: task?.name || 'Mission',
      deliverable,
    });
  }, [tasks, state]);

  const deliverableSubmitModal = useMemo(() => (
    state.deliverableSubmitModal
      ? { ...state.deliverableSubmitModal, onOpenSubmitModal: openSubmitModal }
      : { onOpenSubmitModal: openSubmitModal }
  ), [state.deliverableSubmitModal, openSubmitModal]);

  const onSubmitDeliverableLink = useCallback((deliverableId, link) => {
    const taskId = state.deliverableSubmitModal?.taskId;
    if (!taskId) return;
    const updated = getDeliverables(taskId).map(d =>
      d.id === deliverableId ? { ...d, link, completed: true } : d
    );
    actions.handleDeliverablesChange(taskId, updated);
  }, [actions, getDeliverables, state.deliverableSubmitModal]);

  const onSubmitDeliverableAttachment = useCallback((deliverableId, attachmentData) => {
    const taskId = state.deliverableSubmitModal?.taskId;
    if (!taskId) return;
    const updated = getDeliverables(taskId).map(d =>
      d.id === deliverableId ? {
        ...d,
        completed: true,
        attachmentId: attachmentData.attachmentId,
        submittedFilename: attachmentData.filename,
      } : d
    );
    actions.handleDeliverablesChange(taskId, updated);
  }, [actions, getDeliverables, state.deliverableSubmitModal]);

  return {
    handleDeleteModalConfirm,
    onGanttAddTask,
    onGanttAddDecision,
    onGanttAddWait,
    onAddDeliverable,
    onRemoveDeliverable,
    onUpdateDeliverable,
    onClearDeliverable,
    onAttachmentsChange,
    onCustomFieldsChange,
    deliverableSubmitModal,
    onSubmitDeliverableLink,
    onSubmitDeliverableAttachment,
  };
}
